import '@pocket/web-ui/lib/pocket-web-ui.css'

import React from 'react'
import { useEffect } from 'react'

import withRedux from 'next-redux-wrapper'
import withReduxSaga from 'next-redux-saga'
import { Provider } from 'react-redux'
import { initializeStore } from 'store'
import { ViewportProvider } from '@pocket/web-ui'

import * as Sentry from '@sentry/node'
import { sentrySettings } from 'common/utilities/sentry-settings'

import ReactGA from 'react-ga'
import { initializeSnowplow } from 'common/utilities/snowplow'
import { signalTestsReady } from '../../cypress/support/utils'

import { fetchUserData, userHydrate, devUser } from 'connectors/user/user.state'
import { checkSessGuid, sessGuidHydrate } from 'connectors/user/user.state'

import { featuresHydrate } from 'connectors/feature-flags/feature-flags.state'
import { fetchUnleashData } from 'connectors/feature-flags/feature-flags.state'

import { loadPolyfills } from 'common/utilities/polyfills'
import { loadOptinMonster } from 'common/utilities/external-libraries'
import { appWithTranslation } from 'common/utilities/i18n'

import { DevTools } from 'components/dev-tools/dev-tools'
import DiscoverError from './_error'

import { GOOGLE_ANALYTICS_ID } from 'common/constants'
import { trackPageView } from 'connectors/snowplow/snowplow.state'

const showDevTools = process.env.SHOW_DEV === 'included'

/** Set up Sentry so we may catch errors
 --------------------------------------------------------------- */
Sentry.init(sentrySettings)

/** App
 --------------------------------------------------------------- */
function DiscoverApp({ Component, pageProps, store, err }) {
  // Initialize app once per page load

  useEffect(() => {
    const { dispatch } = store
    const state = store.getState()
    const { user_id, sess_guid } = state.user

    // Load any relevant polyfills
    loadPolyfills()

    // Perform client side auth if flag is enabled !! DEV ONLY
    // We don't want to convolute production with multiple sources
    // of truth (client and server side auth).  But this should allow
    // us to auth locally and in feature branches as a stop gap
    const clientSideAuth = showDevTools
    if (clientSideAuth) dispatch(devUser())

    // Set up Snowplow
    initializeSnowplow(user_id, sess_guid)

    // Track Page View
    dispatch(trackPageView())

    // Set up Google Analytics
    const { path } = pageProps
    ReactGA.initialize(GOOGLE_ANALYTICS_ID)
    ReactGA.pageview(path)

    // Load OptinMonster
    loadOptinMonster()

    // signal to Cypress that React client side has loaded
    // Make sure this is the last thing we fire
    signalTestsReady()
  }, []) //eslint-disable-line react-hooks/exhaustive-deps

  // Render our page with a wrapped provider and optional devTools
  return (
    <Provider store={store}>
      <ViewportProvider>
        {showDevTools ? <DevTools /> : null}
        <Component {...pageProps} err={err} />
      </ViewportProvider>
    </Provider>
  )
}

/** Initial props runs only on the server side
 * NOTE: This section is run in node so we can assume a lack of
 * document, window, and other browser conventions
 --------------------------------------------------------------- */
DiscoverApp.getInitialProps = async ({ Component, ctx }) => {
  // If we are rendering the error we don't want to run through hydration
  if (Component === DiscoverError) return { pageProps: {} }

  // Get the host, url and path so they are available to all pages
  const { req, res, store } = ctx
  const url = req.protocol + '://' + req.get('host') + req.originalUrl
  const path = req.originalUrl // req.originalUrl maintains query params

  // The following functions are deemed critical, render blocking requests
  // ---------------------------------------------------------------

  // On SSR requests we need to pass through cookies explicitly
  const cookie = ctx?.req?.headers?.cookie

  // !! Hydrate user
  const user = await fetchUserData(cookie)
  if (user) store.dispatch(userHydrate(user))

  // !! Hydrate sess_guid
  const sessGuid = await checkSessGuid(cookie, res)
  if (sessGuid) store.dispatch(sessGuidHydrate(sessGuid))

  // !! Hydrate features set with unleash
  const features = await fetchUnleashData(user, sessGuid)
  if (features) store.dispatch(featuresHydrate(features))

  // Wait for getInitialProps to run the component if they exist
  const pageProps = Component.getInitialProps
    ? await Component.getInitialProps(ctx, user, sessGuid, features)
    : {}

  // Return our modified pageProps
  return { pageProps: { ...pageProps, url, path } }
}

/**
 * Export the app.  This wraps the app with a few things:
 * 1. Redux: for managing state
 * 2. ReduxSaga: for managing async state requirements
 * 3. i18Next: for managing localizations
 */
export default withRedux(initializeStore)(
  withReduxSaga(appWithTranslation(DiscoverApp))
)
